import React, { useState, useCallback } from 'react';
import { Swords, ChevronDown, ChevronRight, AlertTriangle, SlidersHorizontal, Copy, Play, RefreshCw, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TARGET_BRANDS, generateAttackerUrls } from '@/lib/phishingLab';
import { runLinkAnalysis } from '@/lib/mlClient';
import { useApp } from '@/context/AppContext';
import { DangerBadge } from './SharedComponents';

const complexityLevels = [
  { value: 'low', label: 'Low', desc: 'Simple typosquatting (char swap, omission, duplication)' },
  { value: 'medium', label: 'Medium', desc: 'Typosquatting + phishing keywords + suspicious TLDs' },
  { value: 'advanced', label: 'Advanced', desc: 'Subdomain chains, numeric subs, homographs, Punycode' },
];

export const AttackerMode = () => {
  const [brand, setBrand] = useState('google');
  const [complexity, setComplexity] = useState('medium');
  const [results, setResults] = useState(null);
  const [expandedUrl, setExpandedUrl] = useState(null);
  const [testResults, setTestResults] = useState({});
  const { addVerification } = useApp();

  const generate = useCallback(() => {
    const salt = Date.now();
    const generated = generateAttackerUrls(brand, complexity, salt);
    setResults(generated);
    setExpandedUrl(null);
    setTestResults({});

    // Persist a Phishing Lab session to history
    addVerification({
      type: 'phishing-lab',
      input: `Attacker · ${generated.brand.display} · ${generated.complexity} · ${generated.urls.length} vectors`,
      verdict: 'Lab Simulation',
      score: 0,
      result: {
        mode: 'attacker',
        brand: generated.brand.name,
        brandDisplay: generated.brand.display,
        complexity: generated.complexity,
        urlCount: generated.urls.length,
      },
    });
  }, [brand, complexity, addVerification]);

  const handleTestUrl = async (url, index) => {
    setTestResults(prev => ({ ...prev, [index]: { loading: true } }));
    const r = await runLinkAnalysis(url);
    setTestResults(prev => ({ ...prev, [index]: r }));
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-4 sm:p-5">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Target Brand</label>
              <select value={brand} onChange={e => setBrand(e.target.value)} data-testid="brand-selector"
                className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm">
                {TARGET_BRANDS.map(b => <option key={b.name} value={b.name}>{b.display}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Attack Complexity</label>
              <div className="flex gap-1.5">
                {complexityLevels.map(cl => (
                  <button key={cl.value} onClick={() => setComplexity(cl.value)} data-testid={`complexity-${cl.value}`}
                    className={cn("flex-1 px-2 py-2 rounded-lg text-xs font-medium transition-all border",
                      complexity === cl.value ? "bg-primary text-primary-foreground border-primary"
                                              : "bg-muted/50 hover:bg-muted border-border text-muted-foreground")}>
                    {cl.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            <SlidersHorizontal className="w-3 h-3 inline mr-1" />
            {complexityLevels.find(c => c.value === complexity)?.desc}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={generate} variant="gradient" className="h-11" data-testid="generate-btn">
              <Swords className="w-4 h-4 mr-2" />Generate Simulation
            </Button>
            {results && (
              <Button onClick={generate} variant="outline" className="h-11" data-testid="regenerate-btn">
                <RefreshCw className="w-4 h-4 mr-2" />Regenerate
              </Button>
            )}
          </div>
        </div>
      </GlassCard>

      {!results && (
        <GlassCard className="p-8 sm:p-12 border-dashed border-2 border-border/60" data-testid="attacker-empty-state">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-destructive/20 to-primary/20 flex items-center justify-center">
              <FlaskConical className="w-7 h-7 text-primary" />
            </div>
            <h4 className="font-display text-base sm:text-lg font-semibold">Ready to simulate an attack</h4>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Pick a target brand and complexity, then click <span className="font-medium text-foreground">Generate Simulation</span> to
              produce a deterministic batch of phishing URLs and walk through the construction steps.
            </p>
          </div>
        </GlassCard>
      )}

      {results && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            {results.urls.length} {results.complexity}-complexity vectors for "{results.brand.display}"
          </h4>
          {results.urls.map((item, idx) => (
            <GlassCard key={idx} className={cn("p-3 sm:p-4 transition-all", expandedUrl === idx && "ring-1 ring-primary/30")}>
              <div className="space-y-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <DangerBadge level={item.dangerLevel} />
                    <span className="text-xs font-medium text-muted-foreground">{item.technique}</span>
                  </div>
                  <code className="text-xs sm:text-sm font-mono text-destructive break-all block">{item.url}</code>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setExpandedUrl(expandedUrl === idx ? null : idx)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-medium bg-muted/50 hover:bg-muted transition-colors">
                    {expandedUrl === idx ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    Step-by-step
                  </button>
                  <button onClick={() => handleTestUrl(item.url, idx)} data-testid={`test-url-${idx}`}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-medium bg-primary/10 hover:bg-primary/20 text-primary transition-colors">
                    <Play className="w-3 h-3" />Test in Link Scorer
                  </button>
                  <button onClick={() => navigator.clipboard?.writeText(item.url)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-medium bg-muted/50 hover:bg-muted transition-colors">
                    <Copy className="w-3 h-3" />Copy
                  </button>
                </div>
                {expandedUrl === idx && (
                  <div className="mt-2 p-3 rounded-lg bg-muted/30 border border-border/50 space-y-1.5">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Construction Steps</p>
                    {item.steps.map((step, si) => (
                      <div key={si} className="flex items-start gap-2">
                        <div className={cn("w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[9px] font-bold",
                          step.type === 'attack' ? 'bg-destructive/20 text-destructive' :
                          step.type === 'result' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground')}>
                          {si + 1}
                        </div>
                        <div className="min-w-0">
                          <span className="text-[10px] text-muted-foreground">{step.label}: </span>
                          <span className={cn("text-[10px] sm:text-xs font-medium break-all",
                            step.type === 'attack' ? 'text-destructive' : step.type === 'result' ? 'text-primary' : '')}>
                            {step.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {testResults[idx] && testResults[idx].loading && (
                  <div className="mt-2 p-3 rounded-lg border bg-muted/30 border-border/50 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    <span className="text-xs text-muted-foreground">Scoring with ML link model…</span>
                  </div>
                )}
                {testResults[idx] && !testResults[idx].loading && (
                  <div className={cn("mt-2 p-3 rounded-lg border",
                    testResults[idx].safetyScore < 50 ? 'bg-destructive/5 border-destructive/30' :
                    testResults[idx].safetyScore < 80 ? 'bg-warning/5 border-warning/30' : 'bg-success/5 border-success/30')}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">Link Safety Score:</span>
                      <span className={cn("text-sm font-bold",
                        testResults[idx].safetyScore < 50 ? 'text-destructive' :
                        testResults[idx].safetyScore < 80 ? 'text-warning' : 'text-success')}>
                        {testResults[idx].safetyScore}/100
                      </span>
                      <Badge variant={testResults[idx].verdict === 'Safe' ? 'safe' : testResults[idx].verdict === 'Caution' ? 'suspicious' : 'danger'} className="text-[10px]">
                        {testResults[idx].verdict}
                      </Badge>
                      <Badge variant="unknown" className="text-[9px]">
                        {testResults[idx].ml?.source === 'model' ? 'ML model' : 'heuristic'}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Same engine as the Link Verification tab.</p>
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};
